import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ShadingType,
} from 'docx'
import { saveAs } from 'file-saver'

const PRIMARY_BLUE = '003366'
const ACCENT_BLUE = '3B82F6'
const AMBER = 'F59E0B'
const RED = 'EF4444'
const GRAY_LIGHT = 'F1F5F9'

function heading(text: string, level: typeof HeadingLevel[keyof typeof HeadingLevel] = HeadingLevel.HEADING_1, color = PRIMARY_BLUE) {
  return new Paragraph({
    heading: level,
    spacing: { before: 300, after: 200 },
    children: [
      new TextRun({
        text,
        bold: true,
        color,
        size: level === HeadingLevel.HEADING_1 ? 36 : level === HeadingLevel.HEADING_2 ? 28 : 24,
      }),
    ],
  })
}

function paragraph(text: string, opts: { bold?: boolean; size?: number; color?: string; align?: typeof AlignmentType[keyof typeof AlignmentType] } = {}) {
  return new Paragraph({
    alignment: opts.align,
    spacing: { after: 150 },
    children: [
      new TextRun({
        text,
        bold: opts.bold,
        size: opts.size ?? 22,
        color: opts.color ?? '1F2937',
      }),
    ],
  })
}

function bullet(text: string) {
  return new Paragraph({
    bullet: { level: 0 },
    spacing: { after: 100 },
    children: [new TextRun({ text, size: 22, color: '1F2937' })],
  })
}

function makeTable(headers: string[], rows: (string | number)[][], headerColor = PRIMARY_BLUE) {
  const headerRow = new TableRow({
    tableHeader: true,
    children: headers.map(
      (h) =>
        new TableCell({
          shading: { type: ShadingType.CLEAR, color: 'auto', fill: headerColor },
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new TextRun({ text: h, bold: true, color: 'FFFFFF', size: 22 })],
            }),
          ],
        })
    ),
  })

  const dataRows = rows.map(
    (row, idx) =>
      new TableRow({
        children: row.map(
          (cell) =>
            new TableCell({
              shading: idx % 2 === 0 ? { type: ShadingType.CLEAR, color: 'auto', fill: GRAY_LIGHT } : undefined,
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [new TextRun({ text: String(cell), size: 22 })],
                }),
              ],
            })
        ),
      })
  )

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [headerRow, ...dataRows],
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4, color: 'CBD5E1' },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: 'CBD5E1' },
      left: { style: BorderStyle.SINGLE, size: 4, color: 'CBD5E1' },
      right: { style: BorderStyle.SINGLE, size: 4, color: 'CBD5E1' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: 'E2E8F0' },
      insideVertical: { style: BorderStyle.SINGLE, size: 2, color: 'E2E8F0' },
    },
  })
}

function spacer() {
  return new Paragraph({ children: [new TextRun('')], spacing: { after: 200 } })
}

export async function exportToDOCX() {
  const doc = new Document({
    creator: 'Teltronic',
    title: 'Apresentação de Indicadores PCDF',
    description: 'Indicadores de Incidentes - Cliente PCDF',
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 22 } },
      },
    },
    sections: [
      {
        properties: {},
        children: [
          // Cover
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 1200, after: 200 },
            children: [
              new TextRun({ text: 'TELTRONIC', bold: true, size: 56, color: PRIMARY_BLUE }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 600 },
            children: [
              new TextRun({ text: 'Telecomunicações & Tecnologia', italics: true, size: 24, color: '64748B' }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 600, after: 200 },
            children: [
              new TextRun({ text: 'APRESENTAÇÃO DE INDICADORES', bold: true, size: 44, color: '1F2937' }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
            children: [
              new TextRun({ text: 'Cliente: PCDF — Polícia Civil do Distrito Federal', size: 28, color: PRIMARY_BLUE }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 1200 },
            children: [
              new TextRun({ text: 'Fonte de Dados: Ferramenta GLPI', size: 22, color: '64748B', italics: true }),
            ],
          }),

          // Section 1
          heading('1. Visão Geral dos Incidentes'),
          paragraph(
            'A análise dos registros de incidentes coletados através da ferramenta GLPI identificou um total de 80 registros no período avaliado. Destes, 52 ocorreram nos pontos de operação (ERBs), representando aproximadamente 65% do total.',
          ),
          spacer(),
          makeTable(
            ['Categoria', 'Quantidade', 'Percentual'],
            [
              ['Incidentes em ERBs (Pontos de Operação)', 52, '65%'],
              ['Incidentes em Outros Locais', 28, '35%'],
              ['Total Geral', 80, '100%'],
            ],
          ),
          spacer(),
          paragraph('Falhas Massivas:', { bold: true, size: 24, color: AMBER }),
          paragraph(
            'Dos 80 registros, 3 incidentes foram classificados como falhas massivas, ou seja, afetaram múltiplos pontos de operação simultaneamente. Esses eventos demandam atenção especial pelo alto impacto operacional.',
          ),

          // Section 2
          heading('2. Locais Mais Afetados'),
          paragraph('Os três sites com maior número de incidentes registrados no GLPI foram:'),
          spacer(),
          makeTable(
            ['Posição', 'Localidade', 'Incidentes'],
            [
              ['1º', '14ª DP', 12],
              ['2º', 'CEPOM PMDF (17ª DP)', 11],
              ['3º', '16º BPM (18ª DP)', 10],
              ['—', 'Demais Sites', 47],
              ['', 'Total', 80],
            ],
          ),

          // Section 3
          heading('3. Causadores de Indisponibilidade'),
          paragraph(
            'Dentre os incidentes registrados, foram identificados os principais pontos de impacto que causam indisponibilidade ao cliente PCDF. As três causas mais relevantes são apresentadas a seguir.',
          ),
          spacer(),
          makeTable(
            ['Causa Raiz', 'Ocorrências'],
            [
              ['Falha de Link (Rede de Transporte)', 28],
              ['Falha de Energia Elétrica', 22],
              ['Falha de Hardware', 18],
              ['Outras Causas', 12],
            ],
          ),

          // Section 4
          heading('4. Indisponibilidade por Falha no Link', HeadingLevel.HEADING_2, ACCENT_BLUE),
          paragraph('Distribuição por localidade dos incidentes causados por falha na rede de transporte:'),
          spacer(),
          makeTable(
            ['Localidade', 'Ocorrências'],
            [
              ['14ª DP', 8],
              ['CEPOM PMDF', 7],
              ['16º BPM', 6],
              ['5ª DP', 4],
              ['23ª DP', 3],
            ],
            ACCENT_BLUE,
          ),

          // Section 5
          heading('5. Indisponibilidade por Falha Elétrica', HeadingLevel.HEADING_2, AMBER),
          paragraph('Distribuição por localidade dos incidentes causados por falhas no fornecimento de energia elétrica:'),
          spacer(),
          makeTable(
            ['Localidade', 'Ocorrências'],
            [
              ['14ª DP', 5],
              ['17ª DP', 4],
              ['18ª DP', 4],
              ['12ª DP', 3],
              ['8ª DP', 2],
            ],
            AMBER,
          ),

          // Section 6
          heading('6. Falhas de Hardware por Localidade', HeadingLevel.HEADING_2, RED),
          paragraph('Principais localidades que apresentaram falha de hardware nos equipamentos:'),
          spacer(),
          makeTable(
            ['Localidade', 'Ocorrências'],
            [
              ['14ª DP', 4],
              ['CEPOM PMDF', 3],
              ['16º BPM', 3],
              ['21ª DP', 2],
              ['7ª DP', 2],
            ],
            RED,
          ),

          // Section 7
          heading('7. Recomendações e Plano de Mitigação'),
          paragraph(
            'Com base na análise dos incidentes e considerando as principais causas de indisponibilidade identificadas, sugerimos as seguintes ações para mitigar a continuidade dos principais incidentes:',
          ),
          spacer(),
          paragraph('7.1 Redundância de Link', { bold: true, size: 24, color: ACCENT_BLUE }),
          bullet('Implementar redundância de operadora nos sites críticos (14ª DP, CEPOM PMDF e 16º BPM).'),
          bullet('Configurar failover automático entre links principais e secundários.'),
          bullet('Estabelecer SLA mais rígido com as operadoras de telecomunicações.'),

          paragraph('7.2 Backup de Energia Elétrica', { bold: true, size: 24, color: AMBER }),
          bullet('Instalar e/ou expandir nobreaks nas ERBs com maior incidência de falha elétrica.'),
          bullet('Avaliar a instalação de geradores de energia para sites estratégicos.'),
          bullet('Implementar monitoramento remoto da autonomia das baterias dos nobreaks.'),

          paragraph('7.3 Renovação e Manutenção de Hardware', { bold: true, size: 24, color: RED }),
          bullet('Mapear equipamentos com falhas recorrentes e elaborar plano de substituição preventiva.'),
          bullet('Manter spare parts (peças de reposição) em locais estratégicos para reduzir o MTTR.'),
          bullet('Revisar contratos de manutenção e suporte com fornecedores de hardware.'),

          paragraph('7.4 Monitoramento Proativo', { bold: true, size: 24, color: PRIMARY_BLUE }),
          bullet('Aprimorar a observabilidade dos ativos via ferramentas como Zabbix, Nagios ou similares.'),
          bullet('Configurar alertas preditivos baseados em SNMP e telemetria.'),
          bullet('Estabelecer dashboards em tempo real para acompanhamento dos KPIs operacionais.'),

          paragraph('7.5 Plano de Contingência contra Falhas Massivas', { bold: true, size: 24, color: AMBER }),
          bullet('Investigar a causa-raiz dos 3 incidentes de falha massiva já registrados.'),
          bullet('Elaborar runbook específico para isolar o impacto e acelerar a recuperação multi-site.'),
          bullet('Realizar simulações periódicas de cenários de falha massiva.'),

          paragraph('7.6 Foco nos Top 3 Sites Mais Afetados', { bold: true, size: 24, color: PRIMARY_BLUE }),
          bullet('Realizar visita técnica detalhada nas localidades 14ª DP, CEPOM PMDF e 16º BPM.'),
          bullet('Identificar fatores ambientais, estruturais e operacionais comuns.'),
          bullet('Elaborar plano de ação específico para cada um desses sites.'),

          spacer(),
          spacer(),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 600 },
            border: {
              top: { style: BorderStyle.SINGLE, size: 6, color: PRIMARY_BLUE },
            },
            children: [
              new TextRun({
                text: 'Apresentação elaborada pela Teltronic',
                bold: true,
                size: 22,
                color: PRIMARY_BLUE,
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: 'Cliente: PCDF · Fonte: GLPI',
                size: 20,
                color: '64748B',
                italics: true,
              }),
            ],
          }),
        ],
      },
    ],
  })

  const blob = await Packer.toBlob(doc)
  saveAs(blob, 'Apresentacao_Indicadores_PCDF_Teltronic.docx')
}
